import { IconChevronLeft } from '@aws-amplify/ui-react/internal'
import { Box } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ButtonBar from '@/components/inputs/ButtonBar'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import BidOverviewButton from '@/features/bidding/components/BidOverviewButton'
import CountrySelector from '@/features/bidding/components/CountrySelector'
import CreateBidButton from '@/features/bidding/components/CreateBidButton'
import OfferedPriceVolumeInvalidTooltip from '@/features/bidding/components/OfferedPriceVolumeInvalidTooltip'
import PriceDataGrid from '@/features/bidding/components/price/offer/PriceDataGrid'
import { useBidOfferPriceActions_Aggregated } from '@/features/bidding/hooks/useBidOfferPriceActions_Aggregated'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { buildBidCapacityLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'

type Props = {
  groupParams: GroupedBidParams
  draftBidsMatchingActivationGroups: ExtendedBid[]
  initialAggregatedBid: AggregatedBid
}

const BidOfferPriceAggregated = ({ groupParams, draftBidsMatchingActivationGroups, initialAggregatedBid }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const actions = useBidOfferPriceActions_Aggregated({ groupParams })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [aggregatedBid, setAggregatedBid] = useState<AggregatedBid>(initialAggregatedBid)

  const handleOnSubmit = async () => {
    setIsSubmitting(true)
    const validationResult = actions.validate({
      aggregatedBid,
      draftBids: draftBidsMatchingActivationGroups,
    })

    if (!validationResult.isValid) {
      // Validation errors are already checked by ptuVolumesAreValid
      // This is an extra safety check
      setIsSubmitting(false)
      return
    }

    await actions
      .save({
        aggregatedBid,
        draftBids: draftBidsMatchingActivationGroups,
      })
      .finally(() => setIsSubmitting(false))
  }

  const ptuVolumesAreValid = actions.validate({
    aggregatedBid,
    draftBids: draftBidsMatchingActivationGroups,
  }).isValid

  return (
    <>
      <PageHeader
        appBarContent={
          <Box alignItems="center" display="flex" flexDirection="row" justifyContent="space-between" width="100%">
            <CountrySelector disabled />
            <BidOverviewButton />
          </Box>
        }
        breadcrumbItems={[
          { text: t('component.page_header.bidding'), to: '/bidding' },
          { text: t('component.page_header.bid.todo_list'), to: '/bidding/todo-list' },
          {
            text: t('component.page_header.bid.create.label', {
              portfolio: groupParams.portfolioCode,
              marketProgram: t(`bidding.market_program.${groupParams.marketProgram}`),
              bidType: groupParams.bidType.toLowerCase(),
            }),
            to: buildBidCapacityLink(groupParams),
          },
        ]}
        pageTitle={t('bidding.create_bid.price_bid')}
      />

      <PriceDataGrid
        activationGroupUuids={draftBidsMatchingActivationGroups.map((bid) => bid.activationGroupUuid)}
        aggregatedBid={aggregatedBid}
        groupParams={groupParams}
        isLoading={false}
        onAggregatedBidChange={setAggregatedBid}
      />

      <ButtonBar>
        <CustomButton
          key="backButton"
          startIcon={<IconChevronLeft />}
          variant={'outlined'}
          onClick={() => navigate(-1)}
        >
          {t('common.navigation.back')}
        </CustomButton>
        <OfferedPriceVolumeInvalidTooltip disabled={ptuVolumesAreValid}>
          <CreateBidButton
            dialogProps={{
              description: t('bidding.dialog.offered_volume_out_of_biddable_volume_range.description'),
              title: t('bidding.dialog.offered_volume_out_of_biddable_volume_range.title'),
            }}
            disabled={isSubmitting || actions.isSaving || !ptuVolumesAreValid}
            onSubmit={handleOnSubmit}
          />
        </OfferedPriceVolumeInvalidTooltip>
      </ButtonBar>
    </>
  )
}

export default BidOfferPriceAggregated
